
import { Vue, Component, Prop, Emit } from "vue-property-decorator"
import api from "../../honcierge-api"
import Base64Uploader from "@/components/Base64Uploader.vue"
import DeleteButtonWithConfirm from "@/components/DeleteButtonWithConfirm.vue"
import { Curator, Image, Writer } from "types/index"
import { DataTableHeader } from "vuetify/types"
import { AxiosError } from "axios"

@Component({ components: { Base64Uploader, DeleteButtonWithConfirm } })
export default class EditDialog extends Vue {
  @Prop({ default: null })
  private id!: number | null
  @Prop({ default: 1 })
  private page!: number

  private name = ""
  private position = ""
  private introduction = ""
  private comment = ""
  private thumbnailImage = { content: "", filename: "" }
  private twitterUrl: Curator["data"]["twitterUrl"] = null
  private instagramUrl: Curator["data"]["instagramUrl"] = null
  private youtubeUrl: Curator["data"]["youtubeUrl"] = null
  private linkUrl: Curator["data"]["linkUrl"] = null
  private articles: Curator["data"]["articles"] = []
  private displayOrder: Curator["data"]["displayOrder"] = 0

  private dialog = false

  // rules for form
  private nameRules = [(name: string) => !!name || "名前は必須です"]
  private positionRules = [
    (position: string) => !!position || "肩書きは必須です",
  ]

  get headers(): DataTableHeader[] {
    return [
      { text: "編集", value: "", sortable: false },
      { text: "ステータス", value: "status" },
      { text: "公開日", value: "publishedAt" },
      { text: "コンシェルジュ", value: "writer" },
      { text: "画像", value: "thumbnailImage", sortable: false },
      { text: "更新日時", value: "updatedAt" },
    ]
  }

  public createOrUpdateCurator(): void {
    if (!this.validate()) {
      return
    }
    const curator: Curator["view"] = {
      name: this.name,
      position: this.position,
      introduction: this.introduction,
      comment: this.comment,
      thumbnailImage: this.thumbnailImage,
      twitterUrl: this.twitterUrl || null,
      instagramUrl: this.instagramUrl || null,
      youtubeUrl: this.youtubeUrl || null,
      linkUrl: this.linkUrl || null,
      displayOrder: this.displayOrder,
    }
    if (this.id !== null) {
      // 更新
      curator.id = this.id
      api
        .updateCurator(curator)
        .then((res) => {
          this.emitCurator(res.data.curator)
        })
        .catch((e: AxiosError) => {
          if (e.response) {
            e.response.data.messages.forEach((messsage: string) => {
              this.$toasted.error(messsage)
            })
          }
        })
    } else {
      // 新規作成
      if (
        this.thumbnailImage &&
        (!this.thumbnailImage.filename || !this.thumbnailImage.content)
      ) {
        this.$toasted.error("画像を指定してください")
        return
      }
      api
        .createCurator(curator)
        .then((res) => {
          this.emitCurator(res.data.curator)
        })
        .catch((e: AxiosError) => {
          if (e.response) {
            e.response.data.messages.forEach((message: string) => {
              this.$toasted.error(message)
            })
          }
        })
    }
  }

  private clear() {
    this.init()
    this.resetValidation()
  }

  private close() {
    this.dialog = false
    this.clear()
  }

  @Emit("emitCurator")
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private emitCurator(curator: Curator["view"]) {
    this.close()
  }

  @Emit("emitDeleteCurator")
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private emitDeleteCurator(page: number) {
    this.close()
  }

  private init() {
    this.name = ""
    this.position = ""
    this.introduction = ""
    this.comment = ""
    this.thumbnailImage = { content: "", filename: "" }
    const base64Uploader = this.$refs.base64Uploader as InstanceType<
      typeof Base64Uploader
    >
    base64Uploader.filename = ""
    this.twitterUrl = null
    this.instagramUrl = null
    this.youtubeUrl = null
    this.linkUrl = null
    this.displayOrder = 0
    this.articles.length = 0
  }

  private loadImage(content: string, filename: string) {
    this.thumbnailImage.content = content
    this.thumbnailImage.filename = filename
  }

  public async open() {
    try {
      if (this.id !== null) {
        const response = await api.getCurator(this.id)
        const curator = response.data.curator

        this.name = curator.name
        this.position = curator.position
        this.introduction = curator.introduction
        this.comment = curator.comment
        this.twitterUrl = curator.twitterUrl
        this.instagramUrl = curator.instagramUrl
        this.youtubeUrl = curator.youtubeUrl
        this.linkUrl = curator.linkUrl
        this.displayOrder = curator.displayOrder
        this.articles = curator.articles
      }
      this.dialog = true
    } catch {
      this.$toasted.error("著名人情報の取得に失敗しました")
    }
  }

  private deleteCurator() {
    if (this.id !== null) {
      api
        .deleteCurator(this.id)
        .then(() => {
          this.emitDeleteCurator(this.page)
        })
        .catch((error: AxiosError) => {
          console.log(error.stack)
          if (error.response) {
            this.$toasted.error(error.response.data.messages)
          }
        })
    }
  }

  private resetValidation(): void {
    const form = this.$refs.form as InstanceType<typeof EditDialog>
    return form.resetValidation()
  }

  private validate(): boolean {
    const form = this.$refs.form as InstanceType<typeof EditDialog>
    return form.validate()
  }

  private existWriterThumbnail(writer: Writer | null): boolean {
    if (writer == null) {
      return false
    }
    return writer.thumbnail.filename !== "" && writer.thumbnail.url !== ""
  }

  private existArticleThumbnail(thumbnail: Image): boolean {
    return thumbnail.filename !== "" && thumbnail.url !== ""
  }
}
